import TextField from '@mui/material/TextField';
import Autocomplete, {AutocompleteProps} from '@mui/material/Autocomplete';
import React from 'react';
import {useController, useFormContext} from 'react-hook-form';
import {SetValueOptions} from 'components/hook-form/helpers';
import {isArray} from 'lodash';
import {useRegisterLabel} from 'common/context/FormContext';
import {IDiplomatObject} from 'types/DiplomatObject';

type AutocompleteOption = {id: string; name: string} | string;

type FormikMultiselectProps<T> = {
   name: string;
   label?: string;
   placeholder?: string;
   options: AutocompleteOption[];
   disabled?: boolean;
   freeSolo?: boolean;
   error?: string;
   groupBy?: AutocompleteProps<T, true, false, true>['groupBy'];
};

type Props<T> = FormikMultiselectProps<T> & {
   value: string[];
   setValue: (value: string[]) => void;
};

const Multiselect = React.memo(
   ({
      name,
      label,
      placeholder,
      disabled,
      options,
      freeSolo,
      value,
      setValue,
      error,
   }: Props<any>) => {
      const [autocompleteValue, setAutocompleteValue] = React.useState<AutocompleteOption[]>([]);

      React.useEffect(() => {
         const parsedValue = value
            ? value.map(
                 (value) =>
                    options.find((option) => {
                       if (typeof option === 'object') {
                          return option.id === value;
                       }
                       return option === value;
                    })!
              )
            : [];
         setAutocompleteValue(parsedValue);
      }, [value, options]);

      const getOptionLabel = (option: AutocompleteOption) => {
         if (typeof option === 'object') {
            return option.name;
         }
         return option;
      };

      const handleChange = (_: React.ChangeEvent<{}>, value: AutocompleteOption[]) => {
         _.preventDefault();
         _.stopPropagation();
         setValue(
            value.map((val) => {
               if (typeof val === 'object') {
                  return val.id;
               }
               return val;
            })
         );
      };

      console.debug('Multiselect', error);

      return (
         <Autocomplete
            multiple
            fullWidth
            freeSolo={freeSolo}
            id={`${name}-autocomplete`}
            options={options}
            value={autocompleteValue}
            filterSelectedOptions
            disabled={disabled}
            getOptionLabel={getOptionLabel}
            renderInput={(params) => (
               <TextField
                  {...params}
                  variant="outlined"
                  label={label}
                  placeholder={placeholder}
                  error={Boolean(error)}
                  helperText={error}
               />
            )}
            onChange={handleChange}
         />
      );
   }
);

const HookFormMultiselect = (props: FormikMultiselectProps<IDiplomatObject>) => {
   const methods = useController({
      name: props.name,
   });
   useRegisterLabel(props.name, props.placeholder || (props.label as string));

   const {
      field: {value},
      fieldState: {error: _error},
   } = methods;
   const {setValue} = useFormContext();
   const _setValue = React.useCallback(
      (val: string[] | null) => setValue(props.name, val, SetValueOptions),
      [props.name, setValue]
   );
   return (
      <Multiselect
         {...props}
         error={_error?.message}
         setValue={_setValue}
         value={isArray(value) ? value : [value]}
      />
   );
};

export default React.memo(HookFormMultiselect);
